import { html } from "../utils/dom-utils";
import { FilelistManager } from "./file-manager";
import {
  AttachmentTableManager,
  isHpdRestrictedAttachmentType,
  parseAttachmentInput,
} from "./attachment-table";
import { DropdownManager } from "./dropdown-manager";
import { GenericTypeahead } from "../components/typeahead";

// Constants
const FILE_SIZE_UNITS = ["B", "KB", "MB", "GB"];
const FILE_SIZE_THRESHOLD = 1024;
const ERROR_DISPLAY_TIMEOUT_MS = 5000;
// TODO: remove
export const ATTACHMENT_KEY_PREFIX = {
  NEW: "new-",
  EXISTING: "existing-",
};

// Interface for attachment type options
export interface AttachmentTypeOption {
  Id: number;
  Name: string;
  Description: string;
  ProjectProgramAreaId: number;
}

// Interface for file row information
export interface FileRowInfo {
  isExisting: boolean;
  name: string;
  size: number;
  id?: string;
  index: number | null;
  currentAttachmentTypeId?: string;
}

// Sum type for file row data
export type FileRowData =
  | {
      type: "existing";
      file: {
        id: string;
        name: string;
        size: number;
        currentAttachmentTypeId?: string;
      };
    }
  | {
      type: "new";
      file: File;
      index: number;
      currentAttachmentTypeId?: string;
    };

// Pure utility functions extracted outside the class
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";

  const i = Math.floor(Math.log(bytes) / Math.log(FILE_SIZE_THRESHOLD));

  return (
    parseFloat((bytes / Math.pow(FILE_SIZE_THRESHOLD, i)).toFixed(1)) +
    " " +
    FILE_SIZE_UNITS[i]
  );
}

export function isDuplicateFile(file: File, existingFiles: File[]): boolean {
  return existingFiles.some(
    (existing) => existing.name === file.name && existing.size === file.size,
  );
}

// Error handling utilities
function createErrorElement(
  fileInputId: string,
  errors: string[],
): HTMLElement {
  const errorContainer = html`
    <div id="${fileInputId}-errors" class="attachment-errors alert alert-danger mt-2"></div>
  ` as HTMLElement;

  if (errorContainer instanceof Array)
    throw Error("Should only get one element");

  errors.forEach((errorMsg) => {
    const error = html`
      <div class="attachment-error">
        <i class="fas fa-exclamation-triangle me-2"></i>${errorMsg}
      </div>
    ` as HTMLElement;

    errorContainer.appendChild(error);
  });

  return errorContainer;
}

function displayErrors(fileInputId: string, errors: string[]): void {
  // Remove existing error container if present
  const existingContainer = document.getElementById(`${fileInputId}-errors`);
  if (existingContainer) {
    existingContainer.remove();
  }

  // If no errors, just return (container already removed)
  if (errors.length === 0) return;

  // Create and insert new error container
  const attachmentList = document.getElementById(
    `${fileInputId}-attachment-list`,
  );
  if (!attachmentList) return;

  const errorContainer = createErrorElement(fileInputId, errors);
  attachmentList.parentElement!.insertBefore(
    errorContainer,
    attachmentList.nextSibling,
  );
}
export class AttachmentManager {
  private fileInput: HTMLInputElement;
  private fileManager: FilelistManager;
  private attachmentTypeSelections: Map<string, number> = new Map(); // fileId/index -> attachmentTypeId
  private typeaheadContainer: GenericTypeahead;
  private dropdownManager: DropdownManager;
  private attachmentTableManager: AttachmentTableManager;

  constructor(
    fileInput: HTMLInputElement,
    typeaheadContainer: GenericTypeahead,
  ) {
    this.fileInput = fileInput;
    this.typeaheadContainer = typeaheadContainer;
    const templateId = `${fileInput.id}-sync-typeahead-template`;

    const inputInfo = parseAttachmentInput(fileInput);
    this.fileManager = new FilelistManager(inputInfo.existingFiles, inputInfo);
    this.dropdownManager = new DropdownManager(
      inputInfo.attachmentTypes,
      inputInfo.projectProgramAreaId,
      templateId,
      this.typeaheadContainer,
      this.attachmentTypeSelections,
      inputInfo.isHpdStaff,
    );
    this.attachmentTableManager = new AttachmentTableManager(
      this.dropdownManager,
      this.typeaheadContainer,
      templateId,
      this.fileInput.id,
      (btn) => {
        const fileId = btn.getAttribute("data-existing-file-id");
        const fileIndex = parseInt(btn.getAttribute("data-file-index") ?? "-1");
        if (fileId) {
          this.removeExistingFile(fileId);
        } else if (fileIndex != -1) {
          this.removeFile(fileIndex);
        } else {
          throw new Error("Invalid row during delete");
        }
      },
      inputInfo.isHpdStaff,
    );
    this.init();

    const isExistingPIIFile = inputInfo.existingFiles
      .map((f) => parseInt(f.currentAttachmentTypeId ?? ""))
      .map((id) => this.dropdownManager.getAttachmentTypeName(id))
      .some((type) => (type ? isHpdRestrictedAttachmentType(type) : false));

    this.setupProgramAreaWatcher(inputInfo.isHpdStaff, isExistingPIIFile);

    this.setupProgramAreaListener();
  }

  private init(): void {
    this.fileInput.style.display = "none";
    const enhancedInput = this.createEnhancedInput();
    this.fileInput.parentElement!.insertBefore(enhancedInput, this.fileInput);

    // Always update display to render existing files
    this.attachmentTableManager.updateDisplay(this.fileManager.getFileInfo());

    if (this.fileInput.files && this.fileInput.files.length > 0) {
      this.fileManager.addFiles(Array.from(this.fileInput.files));
      this.attachmentTableManager.updateDisplay(this.fileManager.getFileInfo());
    }
  }

  private createEnhancedInput(): HTMLElement {
    const container = html`
      <div class="attachment-input-enhanced">
        <button type="button" class="btn btn-outline-secondary attachment-browse-btn">
          <i class="fas fa-upload me-2" aria-hidden="true"></i>
          Choose Files
        </button>
      </div>
    `;

    if (container instanceof Array) throw Error("Should only get one element");

    const browseBtn = container.querySelector(
      ".attachment-browse-btn",
    ) as HTMLButtonElement;

    browseBtn.addEventListener("click", () => {
      this.fileInput.click();
    });

    this.fileInput.addEventListener("change", () => {
      this.handleFileSelection();
    });

    return container;
  }

  private handleFileSelection(): void {
    const selectedFiles = this.fileInput.files
      ? Array.from(this.fileInput.files)
      : [];

    const errors = this.fileManager.addFiles(selectedFiles);

    // TODO: why is this necessary?
    this.updateFileInput();
    this.syncSelectionsToFileManager();
    this.attachmentTableManager.updateDisplay(this.fileManager.getFileInfo());

    displayErrors(this.fileInput.id, errors);
  }

  private removeFile(index: number): void {
    // TODO: event driven update display
    this.fileManager.removeFile(index);
    this.updateFileInput();
    this.attachmentTableManager.updateDisplay(this.fileManager.getFileInfo());
  }

  private removeExistingFile(fileId: string): void {
    this.fileManager.removeExistingFile(fileId);
    this.attachmentTableManager.updateDisplay(this.fileManager.getFileInfo());
  }

  private updateFileInput(): void {
    const dt = new DataTransfer();
    this.fileManager.getCurrentFiles().forEach((file) => dt.items.add(file));
    this.fileInput.files = dt.files;

    this.attachmentTableManager.updateDisplay(this.fileManager.getFileInfo());
  }

  public getExistingFileIds(): string[] {
    return this.fileManager.getExistingFileIds();
  }

  public getAttachmentTypeSelections(): { [key: string]: number } {
    const selections: { [key: string]: number } = {};
    this.attachmentTypeSelections.forEach((value, key) => {
      selections[key] = value;
    });
    return selections;
  }

  // This is overkill, checking everything, but it works
  private syncSelectionsToFileManager(): void {
    // Sync new file selections
    this.attachmentTypeSelections.forEach((attachmentTypeId, key) => {
      if (key.startsWith(ATTACHMENT_KEY_PREFIX.NEW)) {
        const index = parseInt(key.replace(ATTACHMENT_KEY_PREFIX.NEW, ""));
        this.fileManager.updateNewFileAttachmentType(
          index,
          attachmentTypeId.toString(),
        );
      } else if (key.startsWith(ATTACHMENT_KEY_PREFIX.EXISTING)) {
        const fileId = key.replace(ATTACHMENT_KEY_PREFIX.EXISTING, "");
        this.fileManager.updateExistingFileAttachmentType(
          fileId,
          attachmentTypeId.toString(),
        );
      }
    });
  }

  private setupProgramAreaWatcher(
    isHpd: boolean,
    existingPIIFiles: boolean,
  ): void {
    if (isHpd || !existingPIIFiles) return;

    const programAreaSelect = document.querySelector(
      '[data-field-name="Program Area"]',
    ) as HTMLElement;
    if (!programAreaSelect) return;

    programAreaSelect.setAttribute("disabled", "true");

    const parent = programAreaSelect.parentElement;
    if (!parent) return;

    const allDescendants = parent.querySelectorAll(
      'input:not([type="hidden"]), button',
    );
    const disabledTitle =
      "This project contains sensitive documents. Please delete them and save the project in order to update the Program Area.";

    allDescendants.forEach((element) => {
      element.setAttribute("disabled", "true");
      element.classList.add("text-muted");
      (element as HTMLElement).title = disabledTitle;
    });

    programAreaSelect.title = disabledTitle;
  }

  private setupProgramAreaListener(): void {
    // Listen for program area changes
    document.addEventListener(
      "programAreaChanged",
      (event: CustomEventInit) => {
        const { programAreaId, initial } = event.detail;
        this.dropdownManager.updateProgramAreaId(programAreaId);

        if (initial) {
          // On initial load, just update dropdown options without clearing values
          this.attachmentTableManager.updateAllDropdowns(
            this.fileManager.getFileInfo(),
          );
        } else {
          // On user-initiated change, clear attachment types and rebuild display
          this.fileManager.clearExistingAttachmentTypes();
          this.attachmentTableManager.updateDisplay(
            this.fileManager.getFileInfo(),
          );
        }
      },
    );
  }

  public validateAttachmentTypes(): string[] {
    const errors: string[] = [];

    const currentFiles = this.fileManager.getCurrentFiles();
    const existingFiles = this.fileManager.getExistingFiles();

    // Check new files
    for (let i = 0; i < currentFiles.length; i++) {
      if (
        !this.attachmentTypeSelections.has(`${ATTACHMENT_KEY_PREFIX.NEW}${i}`)
      ) {
        errors.push(
          `Please select an attachment type for ${currentFiles[i].name}`,
        );
      }
    }

    // Check existing files
    for (const existingFile of existingFiles) {
      if (
        !this.attachmentTypeSelections.has(
          `${ATTACHMENT_KEY_PREFIX.EXISTING}${existingFile.id}`,
        )
      ) {
        errors.push(
          `Please select an attachment type for ${existingFile.name}`,
        );
      }
    }

    return errors;
  }
}
